import React, { useRef, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import {
  Box,
  Menu,
  MenuButton,
  MenuGroup,
  MenuList,
  MenuItem,
  Button,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Link } from '../Link';

export default function DownloadLinks(props) {
  const { downloads, meta } = useStaticQuery(DOWNLOADLINKS_QUERY);
  const { variant, ...restProps } = props;

  const menuItemRefs = useRef([]); // Refs for all menu items
  const firstMenuItemRef = useRef(null); // Ref for the first menu item

  const hoverStyles = {
    textDecoration: 'underline',
    color: 'navy',
    background: 'transparent',
  };

  const focusStyles = {
    outline: 'none',
    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
    backgroundColor: '#edf2f7',
  };

  const handleMenuOpen = () => {
    if (firstMenuItemRef.current) {
      firstMenuItemRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    const currentIndex = menuItemRefs.current.indexOf(document.activeElement);
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % menuItemRefs.current.length;
      menuItemRefs.current[nextIndex].focus();
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      const prevIndex =
        (currentIndex - 1 + menuItemRefs.current.length) % menuItemRefs.current.length;
      menuItemRefs.current[prevIndex].focus();
    }
  };

  useEffect(() => {
    const menuItems = menuItemRefs.current;
    menuItems.forEach((item) => {
      if (item) {
        item.addEventListener('keydown', handleKeyDown);
      }
    });

    return () => {
      menuItems.forEach((item) => {
        if (item) {
          item.removeEventListener('keydown', handleKeyDown);
        }
      });
    };
  }, [menuItemRefs.current]); // Re-run when menuItemRefs.current changes

  if (variant === 'menu') {
    return (
      <Menu onOpen={handleMenuOpen}>
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          padding={0}
          background='transparent'
          textStyle='navItem'
          fontSize='0.875rem'
          fontWeight='700'
          height='auto'
          backgroundColor={{ base: 'navy', md: 'transparent' }}
          color={{ base: 'white', lg: 'black' }}
          _hover={{
            textDecoration: 'none',
            color: { base: '#81E6D9', lg: 'navy' },
          }}
          _active={{
            background: 'transparent',
            color: { base: '#81E6D9', lg: 'navy' },
          }}
          display='inline-block'
        >
          Download The Report
        </MenuButton>
        <MenuList>
          <MenuGroup
            textTransform='uppercase'
            letterSpacing='1px'
            fontSize='12px'
            fontWeight='300'
            title='Select your language'
          >
            {meta?.englishPDF && (
              <MenuItem
                as='a'
                href={`${meta.englishPDF.asset.url}/${meta.englishPDF.asset.originalFilename}`}
                target='_blank'
                rel='noopener noreferrer'
                padding='8px 16px'
                _hover={hoverStyles}
                _focus={focusStyles}
                mb={2}
                ref={(el) => {
                  menuItemRefs.current[0] = el;
                  firstMenuItemRef.current = el; // Set ref to the first menu item
                }}
              >
                English
              </MenuItem>
            )}
            {downloads.links.map((link, index) => (
              <MenuItem
                key={link.slug.current}
                as={Link}
                to={{
                  internal: {
                    section: 'single',
                    slug: {
                      current: link.slug.current,
                    },
                  },
                }}
                padding='8px 16px'
                mb={index === downloads.links.length - 1 ? '0' : '8px'}
                _hover={hoverStyles}
                _focus={focusStyles}
                ref={(el) => {
                  menuItemRefs.current[index + 1] = el; // Assign each MenuItem ref
                }}
              >
                {link.displayTitle}
              </MenuItem>
            ))}
          </MenuGroup>
        </MenuList>
      </Menu>
    );
  }

  return (
    <Box {...restProps}>
      {meta?.englishPDF && (
        <ChakraLink
          href={`${meta.englishPDF.asset.url}/${meta.englishPDF.asset.originalFilename}`}
          target='_blank'
          rel='noopener noreferrer'
          padding={variant === 'footer' ? '0' : '8px 16px'}
          _hover={{
            ...hoverStyles,
          }}
          variant={variant || 'primary'}
        >
          English
        </ChakraLink>
      )}
      {downloads.links.map((link) => (
        <Link
          to={{
            internal: {
              section: 'single',
              slug: {
                current: link.slug.current,
              },
            },
          }}
          key={link.slug.current}
          variant={variant || 'primary'}
          _hover={{
            textDecoration: 'underline',
            color: 'navy',
          }}
        >
          {link.displayTitle}
        </Link>
      ))}
    </Box>
  );
}

const DOWNLOADLINKS_QUERY = graphql`
  query DownloadLinks {
    downloads: allSanityDownload {
      links: nodes {
        displayTitle
        slug {
          current
        }
      }
    }
    meta: sanityMeta {
      englishPDF {
        asset {
          url
          originalFilename
        }
      }
    }
  }
`;
